import React, { useState } from "react";
import { Shield, Calendar, Clock, Star, AlertTriangle } from "lucide-react";
import { usePublicData } from "../../hooks/usePublicData";

export default function PublicMatches() {
  const { data, loading } = usePublicData();
  const [showMoreUpcoming, setShowMoreUpcoming] = useState(false);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!data) return null;

  const { teams, matches, players } = data;
  const getTeam = (id) => teams.find((t) => t.id === id);
  const getPlayer = (id) => players?.find((p) => p.id === id);

  // Group matches by date
  const today = new Date().toISOString().split('T')[0];
  
  const matchesByDate = matches.reduce((acc, match) => {
    const date = match.match_date;
    if (!acc[date]) acc[date] = [];
    acc[date].push(match);
    return acc;
  }, {});

  const sortedDates = Object.keys(matchesByDate).sort();
  const upcomingDates = sortedDates.filter(date => date >= today);
  const pastDates = sortedDates.filter(date => date < today).reverse();

  const nextPlayingDay = upcomingDates[0];

  const formatMatchDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Helper to get goal scorers grouped by player
  const getGoalScorers = (goals) => {
    if (!goals || goals.length === 0) return [];
    const scorerMap = {};
    goals.forEach(goal => {
      const player = getPlayer(goal.player_id);
      if (player) {
        if (!scorerMap[player.id]) {
          scorerMap[player.id] = { name: player.name, goals: 0 };
        }
        scorerMap[player.id].goals += 1;
      }
    });
    return Object.values(scorerMap);
  };

  // Helper to get assists grouped by player
  const getAssists = (goals) => {
    if (!goals || goals.length === 0) return [];
    const assistMap = {};
    goals.forEach(goal => {
      if (goal.assist_player_id) {
        const player = getPlayer(goal.assist_player_id);
        if (player) {
          if (!assistMap[player.id]) {
            assistMap[player.id] = { name: player.name, assists: 0 };
          }
          assistMap[player.id].assists += 1;
        }
      }
    });
    return Object.values(assistMap);
  };

  // Helper to get cards
  const getCardsByTeam = (cards, teamPlayers) => {
    if (!cards || cards.length === 0) return [];
    const teamPlayerIds = teamPlayers?.map(p => p.id) || [];
    return cards.filter(card => {
      const player = getPlayer(card.player_id);
      return player && teamPlayerIds.includes(player.id);
    }).map(card => ({
      player: getPlayer(card.player_id),
      type: card.card_type
    }));
  };

  const renderMatchCard = (match) => {
    const home = getTeam(match.home_team_id);
    const away = getTeam(match.away_team_id);
    const isCompleted = match.status === "completed";
    
    // Get match details for completed matches
    const homeScorers = isCompleted ? getGoalScorers(match.home_goals) : [];
    const awayScorers = isCompleted ? getGoalScorers(match.away_goals) : [];
    const homeAssists = isCompleted ? getAssists(match.home_goals) : [];
    const awayAssists = isCompleted ? getAssists(match.away_goals) : [];
    const homeMvp = isCompleted && match.home_mvp ? getPlayer(match.home_mvp) : null;
    const awayMvp = isCompleted && match.away_mvp ? getPlayer(match.away_mvp) : null;
    
    // Get cards
    const homeCards = isCompleted ? (match.cards || []).filter(c => {
      const player = getPlayer(c.player_id);
      return player && players?.some(p => p.id === player.id && p.team_id === match.home_team_id);
    }).map(c => ({ player: getPlayer(c.player_id), type: c.card_type })) : [];
    
    const awayCards = isCompleted ? (match.cards || []).filter(c => {
      const player = getPlayer(c.player_id);
      return player && players?.some(p => p.id === player.id && p.team_id === match.away_team_id);
    }).map(c => ({ player: getPlayer(c.player_id), type: c.card_type })) : [];

    const hasDetails = isCompleted && (homeScorers.length > 0 || awayScorers.length > 0 || homeAssists.length > 0 || awayAssists.length > 0 || homeMvp || awayMvp || homeCards.length > 0 || awayCards.length > 0);

    return (
      <div key={match.id} className="bg-card border border-border rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs uppercase tracking-wider text-muted-foreground">
            {match.match_type === "league" ? "League" : match.match_type}
          </span>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Clock className="w-3 h-3" />
            {match.match_time}
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1">
            {home?.logo ? (
              <img src={home.logo} alt="" className="w-10 h-10 rounded object-cover" />
            ) : (
              <div className="w-10 h-10 bg-secondary rounded flex items-center justify-center">
                <Shield className="w-5 h-5 text-muted-foreground" />
              </div>
            )}
            <span className="font-medium truncate">{home?.name || "TBD"}</span>
          </div>
          <div className="px-4 text-center">
            {match.status === "completed" ? (
              <span className="font-heading text-2xl font-black">
                {match.home_score} - {match.away_score}
              </span>
            ) : (
              <span className="text-muted-foreground">VS</span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-1 justify-end">
            <span className="font-medium truncate text-right">{away?.name || "TBD"}</span>
            {away?.logo ? (
              <img src={away.logo} alt="" className="w-10 h-10 rounded object-cover" />
            ) : (
              <div className="w-10 h-10 bg-secondary rounded flex items-center justify-center">
                <Shield className="w-5 h-5 text-muted-foreground" />
              </div>
            )}
          </div>
        </div>

        {/* Match Details for Completed Games */}
        {hasDetails && (
          <div className="mt-4 pt-3 border-t border-border/50">
            <div className="grid grid-cols-2 gap-4 text-xs text-muted-foreground">
              {/* Home Team Details */}
              <div className="space-y-1.5">
                {/* Goal Scorers */}
                {homeScorers.length > 0 && (
                  <div className="flex items-start gap-1.5">
                    <span className="text-primary">⚽</span>
                    <span className="opacity-70">
                      {homeScorers.map((s, i) => (
                        <span key={i}>
                          {s.name.split(' ').slice(-1)[0]}{s.goals > 1 ? ` (${s.goals})` : ''}
                          {i < homeScorers.length - 1 ? ', ' : ''}
                        </span>
                      ))}
                    </span>
                  </div>
                )}
                {/* Assists */}
                {homeAssists.length > 0 && (
                  <div className="flex items-start gap-1.5">
                    <span className="text-blue-400">🅰️</span>
                    <span className="opacity-60">
                      {homeAssists.map((a, i) => (
                        <span key={i}>
                          {a.name.split(' ').slice(-1)[0]}{a.assists > 1 ? ` (${a.assists})` : ''}
                          {i < homeAssists.length - 1 ? ', ' : ''}
                        </span>
                      ))}
                    </span>
                  </div>
                )}
                {/* Cards */}
                {homeCards.length > 0 && (
                  <div className="flex items-start gap-1.5">
                    <span>{homeCards.some(c => c.type === 'red') ? '🟥' : '🟨'}</span>
                    <span className="opacity-70">
                      {homeCards.map((c, i) => (
                        <span key={i} className={c.type === 'red' ? 'text-red-400' : 'text-yellow-400'}>
                          {c.player?.name.split(' ').slice(-1)[0]}
                          {i < homeCards.length - 1 ? ', ' : ''}
                        </span>
                      ))}
                    </span>
                  </div>
                )}
                {/* MVP */}
                {homeMvp && (
                  <div className="flex items-center gap-1.5">
                    <Star className="w-3 h-3 text-yellow-500" />
                    <span className="opacity-70">MVP: {homeMvp.name.split(' ').slice(-1)[0]}</span>
                  </div>
                )}
              </div>

              {/* Away Team Details */}
              <div className="space-y-1.5 text-right">
                {/* Goal Scorers */}
                {awayScorers.length > 0 && (
                  <div className="flex items-start gap-1.5 justify-end">
                    <span className="opacity-70">
                      {awayScorers.map((s, i) => (
                        <span key={i}>
                          {s.name.split(' ').slice(-1)[0]}{s.goals > 1 ? ` (${s.goals})` : ''}
                          {i < awayScorers.length - 1 ? ', ' : ''}
                        </span>
                      ))}
                    </span>
                    <span className="text-primary">⚽</span>
                  </div>
                )}
                {/* Assists */}
                {awayAssists.length > 0 && (
                  <div className="flex items-start gap-1.5 justify-end">
                    <span className="opacity-60">
                      {awayAssists.map((a, i) => (
                        <span key={i}>
                          {a.name.split(' ').slice(-1)[0]}{a.assists > 1 ? ` (${a.assists})` : ''}
                          {i < awayAssists.length - 1 ? ', ' : ''}
                        </span>
                      ))}
                    </span>
                    <span className="text-blue-400">🅰️</span>
                  </div>
                )}
                {/* Cards */}
                {awayCards.length > 0 && (
                  <div className="flex items-start gap-1.5 justify-end">
                    <span className="opacity-70">
                      {awayCards.map((c, i) => (
                        <span key={i} className={c.type === 'red' ? 'text-red-400' : 'text-yellow-400'}>
                          {c.player?.name.split(' ').slice(-1)[0]}
                          {i < awayCards.length - 1 ? ', ' : ''}
                        </span>
                      ))}
                    </span>
                    <span>{awayCards.some(c => c.type === 'red') ? '🟥' : '🟨'}</span>
                  </div>
                )}
                {/* MVP */}
                {awayMvp && (
                  <div className="flex items-center gap-1.5 justify-end">
                    <span className="opacity-70">MVP: {awayMvp.name.split(' ').slice(-1)[0]}</span>
                    <Star className="w-3 h-3 text-yellow-500" />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Get only the next playing day for initial view
  const nextPlayingDayMatches = nextPlayingDay ? [nextPlayingDay] : [];
  const remainingUpcomingDates = upcomingDates.filter(date => date !== nextPlayingDay);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-extrabold uppercase tracking-tight">
          Matches
        </h1>
        <p className="text-muted-foreground mt-1">
          {matches.length} total matches • {matches.filter(m => m.status === 'completed').length} completed
        </p>
      </div>

      {/* Next Playing Day - Always Visible */}
      {nextPlayingDay && (
        <div>
          <h3 className="font-heading text-lg font-bold uppercase tracking-wider mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            Next Match Day
          </h3>
          <div className="space-y-6">
            <div>
              <div className="flex items-center gap-2 mb-3 text-primary">
                <span className="bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded uppercase font-bold">
                  Next
                </span>
                <span className="text-sm font-medium">
                  {formatMatchDate(nextPlayingDay)}
                </span>
              </div>
              <div className="space-y-3">
                {matchesByDate[nextPlayingDay]
                  .sort((a, b) => a.match_time.localeCompare(b.match_time))
                  .map(renderMatchCard)}
              </div>
            </div>
          </div>
          
          {/* See More Upcoming Button */}
          {remainingUpcomingDates.length > 0 && (
            <button
              onClick={() => setShowMoreUpcoming(!showMoreUpcoming)}
              className="mt-4 text-sm text-primary hover:text-primary/80 font-medium flex items-center gap-1 transition-colors"
            >
              {showMoreUpcoming ? '▲ Show Less' : `▼ See More Future Games (${remainingUpcomingDates.length} more days)`}
            </button>
          )}
          
          {/* Additional Upcoming Matches - Expandable */}
          {showMoreUpcoming && remainingUpcomingDates.length > 0 && (
            <div className="mt-6 space-y-6 pl-4 border-l-2 border-primary/20">
              {remainingUpcomingDates.map((date) => (
                <div key={date}>
                  <div className="flex items-center gap-2 mb-3 text-muted-foreground">
                    <span className="text-sm font-medium">
                      {formatMatchDate(date)}
                    </span>
                  </div>
                  <div className="space-y-3">
                    {matchesByDate[date]
                      .sort((a, b) => a.match_time.localeCompare(b.match_time))
                      .map(renderMatchCard)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Past Matches - Most Recent First */}
      {pastDates.length > 0 && (
        <div>
          <h3 className="font-heading text-lg font-bold uppercase tracking-wider mb-4 flex items-center gap-2 text-muted-foreground">
            <Clock className="w-5 h-5" />
            Previous Results
          </h3>
          <div className="space-y-6">
            {pastDates.map((date, index) => (
              <div key={date}>
                <div className="flex items-center gap-2 mb-3 text-muted-foreground">
                  {index === 0 && (
                    <span className="bg-muted text-muted-foreground text-xs px-2 py-0.5 rounded uppercase font-bold">
                      Latest
                    </span>
                  )}
                  <span className="text-sm font-medium">{formatMatchDate(date)}</span>
                </div>
                <div className="space-y-3">
                  {matchesByDate[date]
                    .sort((a, b) => a.match_time.localeCompare(b.match_time))
                    .map(renderMatchCard)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {matches.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <Calendar className="w-12 h-12 mx-auto mb-4 opacity-30" />
          <p>No matches scheduled yet</p>
        </div>
      )}
    </div>
  );
}
